/**
 * Deal Tracking Service
 * Logs real-world sales outcomes to make Lumo adaptive
 */

import { supabase } from '../lib/supabase';

/**
 * Log a sales outcome (win/loss/ongoing)
 */
export const logDeal = async (dealData) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get user's team_id from profile
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('primary_team_id')
    .eq('user_id', user.id)
    .single();

  const { data, error } = await supabase
    .from('sales_outcomes')
    .insert({
      user_id: user.id,
      team_id: profile?.primary_team_id || null,
      deal_name: dealData.dealName,
      deal_value: dealData.dealValue || null,
      status: dealData.status, // 'won', 'lost', 'ongoing'
      outcome_date: dealData.outcomeDate || new Date().toISOString().split('T')[0],
      industry: dealData.industry || null,
      techniques_used: dealData.techniquesUsed || [],
      objections_faced: dealData.objectionsFaced || [],
      scripts_used: dealData.scriptsUsed || [],
      what_worked: dealData.whatWorked || null,
      what_didnt_work: dealData.whatDidntWork || null,
      notes: dealData.notes || null,
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    userId: data.user_id,
    teamId: data.team_id,
    dealName: data.deal_name,
    dealValue: data.deal_value,
    status: data.status,
    outcomeDate: data.outcome_date,
    industry: data.industry,
    techniquesUsed: data.techniques_used,
    objectionsFaced: data.objections_faced,
    scriptsUsed: data.scripts_used,
    whatWorked: data.what_worked,
    whatDidntWork: data.what_didnt_work,
    notes: data.notes,
    createdAt: data.created_at,
  };
};

/**
 * Get recent sales outcomes for user
 */
export const getRecentDeals = async (limit = 10) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('sales_outcomes')
    .select('*')
    .eq('user_id', user.id)
    .order('outcome_date', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return data?.map(deal => ({
    id: deal.id,
    dealName: deal.deal_name,
    dealValue: deal.deal_value,
    status: deal.status,
    outcomeDate: deal.outcome_date,
    industry: deal.industry,
    techniquesUsed: deal.techniques_used,
    objectionsFaced: deal.objections_faced,
    scriptsUsed: deal.scripts_used,
    whatWorked: deal.what_worked,
    whatDidntWork: deal.what_didnt_work,
    notes: deal.notes,
    createdAt: deal.created_at,
  })) || [];
};

/**
 * Get user's sales stats (wins, losses, conversion rate)
 */
export const getUserStats = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('user_sales_stats')
    .select('*')
    .eq('user_id', user.id)
    .single();

  // Gracefully handle RLS/empty/no-row cases (PGRST116 is no rows; 406 is RLS blocked)
  if (error) {
    if (error.code === 'PGRST116' || error.code === 'PGRST303' || error?.status === 406) {
      return {
        totalWins: 0,
        totalLosses: 0,
        ongoingDeals: 0,
        totalRevenue: 0,
        winRatePercent: 0,
        lastWinDate: null,
        winsLast30Days: 0,
        winsLast7Days: 0,
      };
    }
    throw error;
  }

  if (!data) {
    return {
      totalWins: 0,
      totalLosses: 0,
      ongoingDeals: 0,
      totalRevenue: 0,
      winRatePercent: 0,
      lastWinDate: null,
      winsLast30Days: 0,
      winsLast7Days: 0,
    };
  }

  return {
    totalWins: data.total_wins || 0,
    totalLosses: data.total_losses || 0,
    ongoingDeals: data.ongoing_deals || 0,
    totalRevenue: parseFloat(data.total_revenue) || 0,
    winRatePercent: parseFloat(data.win_rate_percent) || 0,
    lastWinDate: data.last_win_date,
    winsLast30Days: data.wins_last_30_days || 0,
    winsLast7Days: data.wins_last_7_days || 0,
  };
};

/**
 * Update an existing deal
 */
export const updateDeal = async (dealId, updates) => {
  const { data, error } = await supabase
    .from('sales_outcomes')
    .update({
      deal_name: updates.dealName,
      deal_value: updates.dealValue,
      status: updates.status,
      outcome_date: updates.outcomeDate,
      industry: updates.industry,
      techniques_used: updates.techniquesUsed,
      objections_faced: updates.objectionsFaced,
      scripts_used: updates.scriptsUsed,
      what_worked: updates.whatWorked,
      what_didnt_work: updates.whatDidntWork,
      notes: updates.notes,
    })
    .eq('id', dealId)
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    dealName: data.deal_name,
    dealValue: data.deal_value,
    status: data.status,
    outcomeDate: data.outcome_date,
    // ... rest of camelCase conversion
  };
};

/**
 * Delete a deal
 */
export const deleteDeal = async (dealId) => {
  const { error } = await supabase
    .from('sales_outcomes')
    .delete()
    .eq('id', dealId);

  if (error) throw error;
};

/**
 * Get what techniques are working best (for Lumo adaptive learning)
 */
export const getTopPerformingTechniques = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get all wins with techniques
  const { data, error } = await supabase
    .from('sales_outcomes')
    .select('techniques_used')
    .eq('user_id', user.id)
    .eq('status', 'won')
    .not('techniques_used', 'is', null);

  if (error) throw error;

  // Count technique frequency
  const techniqueCounts = {};
  data?.forEach(deal => {
    deal.techniques_used?.forEach(tech => {
      techniqueCounts[tech] = (techniqueCounts[tech] || 0) + 1;
    });
  });

  // Sort by frequency
  return Object.entries(techniqueCounts)
    .map(([technique, count]) => ({ technique, count }))
    .sort((a, b) => b.count - a.count);
};

/**
 * Get common objections user faces
 */
export const getCommonObjections = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('sales_outcomes')
    .select('objections_faced, status')
    .eq('user_id', user.id)
    .not('objections_faced', 'is', null);

  if (error) throw error;

  const objectionStats = {};
  data?.forEach(deal => {
    deal.objections_faced?.forEach(objection => {
      if (!objectionStats[objection]) {
        objectionStats[objection] = { total: 0, won: 0 };
      }
      objectionStats[objection].total++;
      if (deal.status === 'won') {
        objectionStats[objection].won++;
      }
    });
  });

  return Object.entries(objectionStats)
    .map(([objection, stats]) => ({
      objection,
      timesEncountered: stats.total,
      winRate: stats.total > 0 ? ((stats.won / stats.total) * 100).toFixed(1) : 0,
    }))
    .sort((a, b) => b.timesEncountered - a.timesEncountered);
};

export default {
  logDeal,
  getRecentDeals,
  getUserStats,
  updateDeal,
  deleteDeal,
  getTopPerformingTechniques,
  getCommonObjections,
};
